import { baseApi } from "@/shared/api";
import type { BudgetSummary, ProjectTableResponse, PoolBalances } from "../types";

interface BudgetParams {
  start_date: string;
  end_date: string;
  scope?: "all" | "internal" | "external";
  project_id?: number;
  include_time_series?: boolean;
  interval?: "day" | "week" | "month" | "quarter" | "half_year";
}

interface ProjectTableParams {
  start_date?: string;
  end_date?: string;
}

export const budgetApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBudgetSummary: builder.query<BudgetSummary, BudgetParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("start_date", params.start_date);
        searchParams.set("end_date", params.end_date);
        if (params.scope) searchParams.set("scope", params.scope);
        if (params.project_id !== undefined) searchParams.set("project_id", String(params.project_id));
        if (params.include_time_series) searchParams.set("include_time_series", "true");
        if (params.interval) searchParams.set("interval", params.interval);
        return {
          url: `/api/accounting/budget/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: [{ type: "Budget" as const, id: "SUMMARY" }],
    }),
    getPools: builder.query<PoolBalances, void>({
      query: () => ({
        url: "/api/accounting/pools/",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: ["PoolBalances"],
    }),
    getProjectTable: builder.query<ProjectTableResponse, ProjectTableParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.start_date) searchParams.set("start_date", params.start_date);
        if (params?.end_date) searchParams.set("end_date", params.end_date);
        const queryString = searchParams.toString();
        return {
          url: queryString ? `/api/accounting/project-table/?${queryString}` : "/api/accounting/project-table/",
          method: "GET",
          credentials: "include" as const,
        };
      },
      providesTags: [{ type: "Budget" as const, id: "PROJECT_TABLE" }],
    }),
  }),
});

export const { useGetBudgetSummaryQuery, useGetPoolsQuery, useGetProjectTableQuery } = budgetApi;
